// External libraries
import styled from '@emotion/styled';

// Internal modules
import {
  BORDER_FOCUSED_COLOR,
  ERROR_COLOR,
  ERROR_MESSAGE_BACKGROUND_COLOR,
  INFO_MESSAGE_BACKGROUND_COLOR,
  PRIMARY_COLOR,
  SUCCESS_COLOR,
  SUCCESS_MESSAGE_BACKGROUND_COLOR,
  WARNING_COLOR,
  WARNING_MESSAGE_BACKGROUND_COLOR,
} from '@/styles';
import type { AlertTypes } from './constants';
import { ALERT_TYPE } from './constants';
import type { PageLevelAlertTypes } from '@/types';
import {
  pageLevelBackgroundColor,
  pageLevelTextColor,
  rebrandAlertBackgroundColors,
  rebrandAlertBorderColors,
  rebrandAlertIconColors,
} from './utils';
import { css } from '@emotion/react';

type AlertProps = {
  type: AlertTypes;
};

const AlertContainer = styled.div<AlertProps>`
  background-color: ${({ type }) => {
    switch (type) {
      case ALERT_TYPE.ERROR:
        return ERROR_MESSAGE_BACKGROUND_COLOR;
      case ALERT_TYPE.INFO:
      default:
        return INFO_MESSAGE_BACKGROUND_COLOR;
      case ALERT_TYPE.SUCCESS:
        return SUCCESS_MESSAGE_BACKGROUND_COLOR;
      case ALERT_TYPE.WARNING:
        return WARNING_MESSAGE_BACKGROUND_COLOR;
    }
  }};
  display: flex;
  padding: 12px 18px;
`;

const AlertMessage = styled.p<AlertProps>`
  color: ${({ type }) => {
    switch (type) {
      case ALERT_TYPE.ERROR:
        return ERROR_COLOR;
      case ALERT_TYPE.INFO:
      default:
        return PRIMARY_COLOR;
      case ALERT_TYPE.SUCCESS:
        return SUCCESS_COLOR;
      case ALERT_TYPE.WARNING:
        return WARNING_COLOR;
    }
  }};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
  margin: 0;
`;

type IconButtonProps = {
  styleOverrides: {
    flexBasis: number;
    svg: {
      height: number;
      width: number;
    };
  };
};

const IconButton = styled.button<IconButtonProps>`
  align-items: center;
  background-color: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: auto;
  justify-content: center;
  ${props => props.styleOverrides};
  outline: 0;
  padding: 0;

  &:focus span {
    box-shadow: 0 0 0 3px ${BORDER_FOCUSED_COLOR};
  }
`;

export const PageLevelAlertContainer = styled.div<{
  pageLevelAlertVariant: PageLevelAlertTypes;
  hasRebrandAlertTreatment: boolean;
}>`
  display: flex;
  align-items: start;
  padding: 1rem;
  border-radius: 12px;
  ${({ pageLevelAlertVariant, hasRebrandAlertTreatment }) =>
    hasRebrandAlertTreatment &&
    css`
      border: 1px solid ${rebrandAlertBorderColors[pageLevelAlertVariant]};
      & svg,
      & path,
      & g {
        fill: ${rebrandAlertIconColors[pageLevelAlertVariant]};
      },
    `};
  background-color: ${({ pageLevelAlertVariant, hasRebrandAlertTreatment }) =>
    hasRebrandAlertTreatment
      ? rebrandAlertBackgroundColors[pageLevelAlertVariant]
      : pageLevelBackgroundColor[pageLevelAlertVariant]};
  color: ${({ pageLevelAlertVariant }) =>
    pageLevelTextColor[pageLevelAlertVariant]};
`;

export { AlertContainer, AlertMessage, IconButton };
